import { useLocalization } from "@fluent/react";
import cc from "classcat";
import {
  Button,
  Popover,
  Row,
  Toggle,
  formatNumber,
  Tooltip,
  useBreakpoints,
} from "@narmi/design_system";

import styles from "./TransactionSummary.module.scss";

import type { TransactionSummary as TransactionSummaryType } from "../hooks/useTransactionSummary";

interface TransactionSummaryProps {
  summary: TransactionSummaryType | null;
  isFiltered: boolean;
  caption: string;
  isLoading?: boolean;
  error?: string | null;
  onExport?: () => void;
  exportDisabled?: boolean;
  hasFutureDates?: boolean;
  includeInternalTransfers?: boolean;
  onToggleInternalTransfers?: (isActive: boolean) => void;
}

const formatTileValue = (
  value: number | undefined,
  isLoading: boolean,
  hasError: boolean
): string => {
  if (hasError) return "-";
  if (isLoading || value === undefined) return formatNumber(0, "currency");
  return formatNumber(value / 100, "currency");
};

const Tile = ({
  label,
  value,
  positive,
  isLoading,
  hasError,
}: {
  label: string;
  value: number | undefined;
  positive: boolean;
  isLoading: boolean;
  hasError: boolean;
}) => (
  <div className={styles.tile}>
    <span className={styles.label}>{label}</span>
    <span className={cc([styles.value, { "fontColor--success": positive }])}>
      {formatTileValue(value, isLoading, hasError)}
    </span>
  </div>
);

const Caption = ({
  caption,
  tooltipText,
}: {
  caption: string;
  tooltipText: string;
}) => (
  <div className={styles.caption}>
    <Tooltip text={tooltipText} side="top" maxWidth="250px">
      <span
        className={cc([styles.captionIcon, "narmi-icon-info"])}
        tabIndex={0}
        role="img"
        aria-label={tooltipText}
      />
    </Tooltip>
    <span>{caption}</span>
  </div>
);

const TransferPopoverContent = ({
  isActive,
  onToggle,
  l10n,
}: {
  isActive: boolean;
  onToggle: (isActive: boolean) => void;
  l10n: ReturnType<typeof useLocalization>["l10n"];
}) => (
  <div className={styles.transferPopover}>
    <Row className={styles.transferPopoverHeader} gapSize="l">
      <div>
        <div
          id="live-ledger-summary-transfers-toggle-label"
          className={styles.transferPopoverTitle}
        >
          {l10n.getString("live-ledger-summary-transfers-title")}
        </div>
        <div className={styles.transferPopoverSubtitle}>
          {l10n.getString("live-ledger-summary-transfers-subtitle")}
        </div>
      </div>
      <Row.Item shrink>
        <Toggle
          isActive={isActive}
          onChange={onToggle}
          labelledBy="live-ledger-summary-transfers-toggle-label"
        />
      </Row.Item>
    </Row>
    <p
      className={cc([
        styles.transferPopoverDescription,
        "rounded--all--m",
        "padding--all--s",
      ])}
    >
      {l10n.getString("live-ledger-summary-transfers-description")}
    </p>
  </div>
);

const TransactionSummary = ({
  summary,
  isFiltered,
  caption,
  isLoading = false,
  error = null,
  onExport,
  exportDisabled = false,
  hasFutureDates = false,
  includeInternalTransfers = false,
  onToggleInternalTransfers,
}: TransactionSummaryProps) => {
  const { l10n } = useLocalization();
  const breakpoints = useBreakpoints();
  const isMobile = !breakpoints.m;
  const hasError = Boolean(error) || hasFutureDates;

  const moneyInLabel = l10n.getString("live-ledger-summary-money-in");
  const moneyOutLabel = l10n.getString("live-ledger-summary-money-out");
  const netChangeLabel = l10n.getString("live-ledger-summary-net-change");
  const tooltipText = isFiltered
    ? l10n.getString("live-ledger-summary-tooltip-filtered")
    : l10n.getString("live-ledger-summary-tooltip-default");
  const exportLabel = isFiltered
    ? l10n.getString("live-ledger-summary-export-filtered")
    : l10n.getString("live-ledger-summary-export-default");

  const tiles = (
    <>
      <Tile
        label={moneyInLabel}
        value={summary?.money_in}
        positive
        isLoading={isLoading}
        hasError={hasError}
      />
      <Tile
        label={moneyOutLabel}
        value={summary?.money_out}
        positive={false}
        isLoading={isLoading}
        hasError={hasError}
      />
      <Tile
        label={netChangeLabel}
        value={summary?.net_change}
        positive={(summary?.net_change ?? 0) >= 0}
        isLoading={isLoading}
        hasError={hasError}
      />
    </>
  );

  return (
    <div
      className={cc([
        styles.card,
        isMobile && styles.cardMobile,
        "border--all",
        "rounded--all--l",
        "padding--all--l",
      ])}
    >
      <div className={isMobile ? styles.contentMobile : styles.content}>
        {isMobile && <Caption caption={caption} tooltipText={tooltipText} />}
        <div className={isMobile ? styles.tilesMobile : styles.tiles}>
          {tiles}
        </div>
        {!isMobile && <Caption caption={caption} tooltipText={tooltipText} />}
      </div>
      <Row className={styles.actions}>
        {onToggleInternalTransfers && (
          <Popover
            content={
              <TransferPopoverContent
                isActive={includeInternalTransfers}
                onToggle={onToggleInternalTransfers}
                l10n={l10n}
              />
            }
            side="bottom"
          >
            <Button
              kind="tertiary"
              startIcon="settings"
              type="button"
              size="s"
              ariaLabel={l10n.getString("live-ledger-summary-settings")}
            />
          </Popover>
        )}
        <Button
          kind="tertiary"
          startIcon="download"
          label={exportLabel}
          type="button"
          size="s"
          onClick={onExport}
          disabled={exportDisabled}
        />
      </Row>
    </div>
  );
};

export default TransactionSummary;
